import { ColDef } from 'ag-grid-community';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Incident, IncidentStatus } from '../models/Incident';
import { Badge } from '@shared/components/ui/badge';
import { Shop } from '@shared/components/ui/shop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faHistory } from '@fortawesome/pro-light-svg-icons';
import { useCouriers, Courier } from '../../../Mappings/List/hooks/useMappings';
import { usePermissions } from '@shared/hooks/usePermissions';

interface UseIncidentsAgGridColumnsProps {
  selectedColumns?: string[];
  onRowClick?: (incident: Incident) => void;
  onOpenActions?: (incident: Incident) => void;
  onOpenHistory?: (incident: Incident) => void;
}

// Mapa de colores para estados (siguiendo el patrón de Orders/List)
const statusColorMap: Record<IncidentStatus, { bg: string; text: string }> = {
  pending: {
    // Pendiente - similar a estado 2 (Pendiente) en Orders
    bg: 'bg-card-border light:bg-badge-bg-status-1',
    text: 'text-badge-status-1',
  },
  in_progress: {
    // En progreso - similar a estado 4/5 (En preparación) en Orders
    bg: 'bg-card-border light:bg-bg-warning',
    text: 'text-warning',
  },
  resolved: {
    // Resuelta - similar a estado 7 (Enviado/éxito) en Orders
    bg: 'bg-bg-success',
    text: 'text-success',
  },
  canceled: {
    // Cancelada - similar a estado 9 (Cancelado) en Orders
    bg: 'bg-bg-error',
    text: 'text-error',
  },
};

// Mapa de etiquetas para estados - se obtiene de las traducciones
// Nota: Este mapa se crea dentro del hook para tener acceso a t()

const formatDate = (date?: string | null) => {
  if (!date) {
    return '-';
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString();
};

export const useIncidentsAgGridColumns = ({
  selectedColumns = [],
  onOpenActions,
  onOpenHistory,
}: UseIncidentsAgGridColumnsProps) => {
  const { t } = useTranslation('incidentsModule');
  const { getTeamType } = usePermissions();
  const is3pl = getTeamType() === 'tenant_3pl';
  const { data: couriers = [] } = useCouriers();
  const couriersMap = useMemo(() => {
    const map = new Map<number, Courier>();
    couriers.forEach((c) => map.set(c.id, c));
    return map;
  }, [couriers]);

  const allColumnDefs: ColDef[] = useMemo(() => {
    // Mapa de etiquetas para estados - usando traducciones
    const statusLabelMap: Record<IncidentStatus, string> = {
      pending: t('table.statuses.pending'),
      in_progress: t('table.statuses.in_progress'),
      resolved: t('table.statuses.resolved'),
      canceled: t('table.statuses.canceled'),
    };
    const baseColumns: CustomColDef[] = [
      {
        field: 'order_ref',
        headerName: t('table.order_ref'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.order_ref ?? '',
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          const ref = incident.order_ref?.trim();
          return <span className="text-sm font-medium">{ref ? ref : '-'}</span>;
        },
      },
      {
        field: 'shop_name',
        headerName: t('table.shop_name'),
        sortable: false,
        minWidth: 160,
        valueGetter: (params) => params.data?.shop_name ?? '',
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          const name = incident.shop_name?.trim();
          return <span className="text-sm">{name ? name : '-'}</span>;
        },
      },
      ...(is3pl
        ? ([
            {
              field: 'shop_team_name',
              headerName: t('table.shop_team_name'),
              sortable: false,
              minWidth: 160,
              valueGetter: (params: any) => params.data?.shop_team_name ?? '',
              cellRenderer: (params: any) => {
                const incident = params.data as Incident;
                const name = incident.shop_team_name?.trim();
                return <span className="text-sm">{name ? name : '-'}</span>;
              },
            },
          ] as CustomColDef[])
        : []),
      {
        field: 'tracking_number',
        headerName: t('table.tracking_number'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data?.tracking_number ?? '',
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          const tn = incident.tracking_number?.trim();
          return <span className="text-sm font-mono">{tn ? tn : '-'}</span>;
        },
      },
      {
        field: 'courier',
        headerName: t('table.courier'),
        sortable: false,
        minWidth: 200,
        valueGetter: (params) => params.data.courier?.name || couriersMap.get(Number(params.data.courier_id))?.name,
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          const courier = incident.courier || couriersMap.get(Number(incident.courier_id)) || null;

          if (courier?.name) {
            return <Shop name={courier.name} src={courier.image_hash || courier.logo || undefined} />;
          }
          return <span className="text-sm text-complementary-text">-</span>;
        },
      },
      {
        field: 'external_type',
        headerName: t('table.external_type'),
        sortable: false,
        minWidth: 200,
        valueGetter: (params) => params.data?.external_label ?? '',
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          const label = incident.external_label?.trim() ?? '';

          if (!label) {
            return (
              <Badge variant="outline" className="text-xs bg-bg-error text-error">
                {t('table.unmapped')}
              </Badge>
            );
          }
          return <span className="text-sm font-medium">{label}</span>;
        },
      },
      {
        field: 'status',
        headerName: t('table.status'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data.status,
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          const status = incident.status;
          const color = statusColorMap[status] || {
            bg: 'bg-card-border',
            text: 'text-text',
          };
          return <Badge className={`text-xs ${color.bg} ${color.text}`}>{statusLabelMap[status] || status}</Badge>;
        },
      },
      {
        field: 'report_date',
        headerName: t('table.report_date'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data.report_date,
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          return <span className="text-sm">{formatDate(incident.report_date)}</span>;
        },
      },
      {
        field: 'resolution_date',
        headerName: t('table.resolution_date'),
        sortable: false,
        minWidth: 150,
        valueGetter: (params) => params.data.resolution_date,
        cellRenderer: (params: any) => {
          const incident = params.data as Incident;
          return <span className="text-sm">{formatDate(incident.resolution_date)}</span>;
        },
      },
    ];

    // 🎯 COLUMNA: Acciones (siempre presente, fija a la derecha)
    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 52,
      minWidth: 52,
      maxWidth: 52,
      resizable: false,
      pinned: 'right',
      lockPosition: 'right',
      suppressMovable: true,
      suppressSizeToFit: true,
      suppressAutoSize: true,
      sortable: false,
      cellRenderer: (params: any) => {
        const incident = params.data as Incident;
        const isPending = incident.status === 'pending';

        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();

          // Ejecutar inmediatamente sin delays
          if (isPending && onOpenActions) {
            // Si es pending, abrir modal de acciones
            onOpenActions(incident);
          } else if (!isPending && onOpenHistory) {
            // Si no es pending, abrir modal de historial
            onOpenHistory(incident);
          }
        };

        const icon = isPending ? faPlay : faHistory;
        const title = isPending
          ? t('table.open_actions', 'Abrir acciones elegibles')
          : t('table.open_history', 'Ver historial');

        // Clases condicionales: solo el botón de acciones tiene bg-primary y text-white
        const buttonClasses = isPending
          ? 'bg-primary text-white hover:text-white transition-colors inline-flex items-center justify-center p-2 rounded-md hover:bg-primary'
          : 'text-complementary-text hover:text-text transition-colors inline-flex items-center justify-center p-2 rounded-md hover:bg-card-border';

        return (
          <div className="flex justify-end w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={handleClick}
              className={buttonClasses}
              title={title}
              data-testid={isPending ? 'open-actions-button' : 'open-history-button'}
            >
              <FontAwesomeIcon icon={icon} className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      },
    });

    return baseColumns;
  }, [t, couriersMap, onOpenActions, onOpenHistory, is3pl]);

  const filteredColumnDefs = useMemo(() => {
    const actionsColumn = allColumnDefs.find((col) => col.field === 'actions');

    const selectableColumns = allColumnDefs.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return field !== 'actions';
    });

    if (!selectedColumns.length) {
      const result = [...selectableColumns];
      if (actionsColumn) {
        result.push(actionsColumn);
      }
      return result.reduce<ColDef[]>((acc, column) => {
        if (!column) return acc;
        const field = column.field || column.headerName || 'unknown';
        if (!acc.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
          acc.push(column);
        }
        return acc;
      }, []);
    }

    const selectedDefs = selectableColumns.filter((column) => {
      const field = column.field || column.headerName || 'unknown';
      return selectedColumns.includes(field);
    });

    const combined: ColDef[] = [];
    selectedDefs.forEach((column) => {
      const field = column.field || column.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(column);
      }
    });

    // 🎯 Agregar columna de acciones al final (fija a la derecha)
    if (actionsColumn) {
      const field = actionsColumn.field || actionsColumn.headerName || 'unknown';
      if (!combined.some((existing) => (existing.field || existing.headerName || 'unknown') === field)) {
        combined.push(actionsColumn);
      }
    }

    return combined;
  }, [allColumnDefs, selectedColumns]);

  return {
    allColumnDefs,
    filteredColumnDefs,
  };
};
